import {UyapRequest} from "./UyapRequest";
import {ISorguSonuc} from "./CommonInterfaces";
import app from "@/main";

// DEV-3512: UYAP'ın 19.09.2026 güncellemesiyle Borçlu -> Sorgular altına eklenen "Firma" sorgusu (TDT-3525).
// UYAP ücretsiz sorgu kotasından düşüyor; ücretli sorgu akışıyla (DosyaBorclu.kotaIslemleri) çağrılır.

export interface BorcluFirmaTalep {
    dosyaId: string,
    kisiKurumId: number
}

export interface BorcluFirmaCevap extends ISorguSonuc<IBorcluFirmaSonuc> {
}

export interface IBorcluFirmaSonuc {
    data: {
        durum: boolean,
        liste: Array<IFirmaKaydi>,
        isNew?: boolean
    },
    metadata?: {
        FMTY?: string,
        FMTE?: string,
        FMC?: string
    },
    isNew?: boolean
}

export interface IFirmaKaydi {
    ortaklikTuru: string,       // "Yönetim Kurulu Üyesi"
    aktifPasif: string,         // "Aktif" | "Pasif"
    tCKimlikNo: string,
    merkezSube: string,         // "Merkez"
    tescilDurumu: string,       // "TescilEdilmis"
    adi: string,
    soyadi: string,
    firmaUnvani: string,
    baslangicTarihi: string,    // "19.10.2022"
    bitisTarihi: string,
    tur: string,                // "TEMSILCI"
    pasaportNo: string,
    mersisNo: string,
    yetkiSuresi: string,        // "11.11.2022 tarihine kadar"
    isNew?: boolean
}

export default class BorcluFirma extends UyapRequest<BorcluFirmaTalep, BorcluFirmaCevap> {
    getMethod(): 'GET' | 'POST' {
        return 'POST';
    }

    getSpinnerMessage(){
        return "Borçlu firma bilgileri sorgulanıyor...";
    }

    /**
     * Snapshot deposundaki alan adı (picker `name`). DEV-3512 ile gelen ücretli
     * sorgu; EGM/Takbis/SGK Haciz gibi hem tekil yazıma hem okuma zincirine girer.
     */
    protected snapshotAlani(): string | null {
        return "firma";
    }

    cache() {
        return {
            active: true,
            ttl: (60 * 24), //* 1 gun
            key: "borcluFirma_" + app.$uyapHelper.hash(this.talepData)
        };
    }

    getMockResponse(): any {
        // maskelenmiş gerçek cevap: iki pasif kayıt
        return {"borcluBilgileri":{"kisiTumDVO":{"olumKaydi":false,"isAvukat":false,"isGecerlilikAktifAvukat":false,"isNoter":false,"isTarafUyari":false,"isHakimSavci":false,"mernisDegisiklikVarmi":false,"mernisDegisiklikNedeni":"","yabanci":false,"mavikart":false,"sorguTuru":0,"tcKimlikNo":"9********18","adi":"ME***","soyadi":"SO***","babaAdi":"A***","anaAdi":"S***","dogumTarihiStr":"1996","aktifMernisAdresiSorgusu":true,"hasOlumKaydi":false},"kisiKurumId":999999002,"turu":0},"sorgulayanBilgileri":"Av.***","portalTuru":2,"sorguTarihi":"19/09/2026 11:24","sorguSonucDVO":{"data":{"durum":true,"liste":[{"ortaklikTuru":"Yönetim Kurulu Üyesi","aktifPasif":"Pasif","tCKimlikNo":"99999999918","merkezSube":"Merkez","tescilDurumu":"TescilEdilmis","adi":"ME***","firmaUnvani":"ÖRNEK KONUT YAPI KOOPERATİFİ","baslangicTarihi":"19.10.2022","tur":"TEMSILCI","pasaportNo":"","mersisNo":"0999999999900001","soyadi":"SO***","bitisTarihi":"11.11.2022","yetkiSuresi":"11.11.2022 tarihine kadar","isNew":true},{"ortaklikTuru":"Yönetim Kurulu Üyesi","aktifPasif":"Pasif","tCKimlikNo":"99999999918","merkezSube":"Merkez","tescilDurumu":"TescilEdilmis","adi":"ME***","firmaUnvani":"ÖRNEK KONUT YAPI KOOPERATİFİ","baslangicTarihi":"11.11.2022","tur":"TEMSILCI","pasaportNo":"","mersisNo":"0999999999900001","soyadi":"SO***","bitisTarihi":"13.01.2025","yetkiSuresi":"13.01.2025 tarihine kadar","isNew":true}],"isNew":true},"metadata":{"FMTY":"SUCCESS","FMTE":"İşlem başarıyla gerçekleştirildi!","FMC":"ADALET_SUCCESS"},"isNew":true},"sorgulamaTuru":"Firma","sorguSonucu":true,"hasError":false,"hasData":false,"isMock":false};
    }

    getUrl(): string {
        return this.ImzaClientAddr + 'borclu_bilgileri_goruntule_firma.ajx';
    }

    async parse(data: any): Promise<BorcluFirmaCevap> {
        if (!data || typeof data != "object")
            throw new Error((typeof data == "string" && data.trim()) || "Firma sorgusu sonucu alınamadı");
        const dvo = data.sorguSonucDVO;
        // Hata cevabının şekli bilinmiyor: DVO string/boş geldiyse, FMTY SUCCESS değilse, durum false ise
        // ya da liste yoksa hata sayılır; UYAP'ın mesajı (FMTE veya metin) yazılır.
        if (!dvo || typeof dvo != "object") {
            data.sorguSonucu = false;
            data.message = (typeof dvo == "string" && dvo.trim()) || "Firma sorgusu sonucu alınamadı";
            return data;
        }
        // DEV-3512: sunucudaki Excel (FirmaSheet) ile aynı kural: FMTY yalnız gelmişse ve (kırpılıp büyük/küçük harf
        // gözetmeden) SUCCESS değilse hata sayılır; aksi halde Excel kayıtları yazıp yanına bu hata mesajını koyar.
        const fmty = dvo.metadata?.FMTY;
        const basarili = fmty == null || (typeof fmty == "string" && fmty.trim().toUpperCase() == "SUCCESS");
        const liste = dvo.data?.liste;
        if (!basarili || dvo.data?.durum === false || !Array.isArray(liste)) {
            data.sorguSonucu = false;
            // FMTY SUCCESS iken FMTE "İşlem başarıyla gerçekleştirildi!" olduğu için yalnız hata tipinde kullanılır.
            data.message = (!basarili && typeof dvo.metadata.FMTE == "string" && dvo.metadata.FMTE.trim())
                || (typeof dvo.data == "string" && dvo.data.trim())
                || "Firma sorgusu sonucu alınamadı";
            return data;
        }
        data.sorguSonucu = liste.length > 0;
        data.message = "Borçluya ait firma kaydı " + (data.sorguSonucu ? 'var' : 'bulunamadı');
        return data;
    }

}
